import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { AiOutlineApi } from 'react-icons/ai';
import { IoEnterOutline } from 'react-icons/io5';
import { Button, ButtonToolbar, Stack } from 'rsuite';
import { useAppSelector } from '../store/hook';
import { selectInstances } from '../store/instanceSlice';
import { selectCurrentGroup } from '../store/groupSlice';
import { useLazyGetInstanceAccessUrlQuery } from '../services';
import {
  SNAPSHOT_STATE,
  UPGRADE_STATE,
  extractToken,
  getSafeUrl,
  getSafeUrlWithoutToken
} from '../utils';
import { DraggableDrawer } from './DraggableDrawer';
import { ApiHelper } from './ApiHelper';
import { OsUpgrade } from './OsUpgrade';
import { selectInstancesExtraInfo } from '../store/instanceExtraSlice';
import { selectInstancesStateInfo } from '../store/instanceStateSlice';
import { FunctionMenu } from './FunctionMenu';

interface IProps {
  rowData: Record<string, any>;
  server: string;
  actionConst: Record<string, any>;
}
export const InstanceActions = ({ rowData, server, actionConst }: IProps) => {
  const { t } = useTranslation();
  const instances = useAppSelector(selectInstances);
  const instancesExtraInfo = useAppSelector(selectInstancesExtraInfo);
  const instancesStateInfo = useAppSelector(selectInstancesStateInfo);
  const currentGroup = useAppSelector(selectCurrentGroup);
  const [instance, setInstance] = useState<Record<string, any>>({});
  const [extraInfo, setExtraInfo] = useState<Record<string, any>>({});
  const [isActive, setIsActive] = useState<boolean>(true);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [isForceUpgrade, setIsForceUpgrade] = useState<boolean>(false);
  const [isUpgrading, setIsUpgrading] = useState<boolean>(false);
  const [isNoAccess, setIsNoAccess] = useState<boolean>(false);

  const [showApi, setShowApi] = useState<boolean>(false);
  const [showUpgrade, setShowUpgrade] = useState<boolean>(false);
  const [showEnter, setShowEnter] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  const [openApi, setOpenApi] = useState<boolean>(false);
  const [getInstanceUrl] = useLazyGetInstanceAccessUrlQuery();
  const [stateInfo, setStateInfo] = useState<Record<string, any>>({});
  const STATUS = [
    SNAPSHOT_STATE.SNAPSHOT_START,
    SNAPSHOT_STATE.SNAPSHOT_REBOOT_DONE,
    SNAPSHOT_STATE.REVERT_START
  ];

  useEffect(() => {
    const data = instances?.[currentGroup.id]?.[rowData?.account_id];
    const extra = instancesExtraInfo?.[currentGroup.id]?.[rowData?.account_id];
    const stateInfo =
      instancesStateInfo?.[currentGroup.id]?.[rowData?.account_id];
    if (data) {
      setInstance({ ...data });
      setShowApi(!data?.disable_api);
      setIsExpired(!!data?.is_expired);
      setIsNoAccess(data?.service_setting === 'NO_ACCESS');
    }
    if (extra?.requestStatus === 'pending') {
      setMessage(`${t('common.expandedRow.actionMessage.fetch')}`);
      setShowEnter(false);
    } else if (extra?.requestStatus === 'fulfilled') {
      setIsActive(!!extra?.is_active);
      setIsForceUpgrade(!!extra?.need_force_upgrade);
      setExtraInfo(extra);
    }
    if (stateInfo) {
      setIsUpgrading(stateInfo?.state === UPGRADE_STATE.UPGRADING);
      if (stateInfo?.state === UPGRADE_STATE.UPGRADE_SUCCESS) {
        setIsActive(true);
      }
      // clear cache state for force upgrade instances after starting upgrade or upgrade success
      if (
        stateInfo?.state === UPGRADE_STATE.UPGRADE_SUCCESS ||
        stateInfo?.state === UPGRADE_STATE.UPGRADING
      ) {
        setIsForceUpgrade(false);
      }
      setStateInfo(stateInfo);
    }
  }, [instances, instancesExtraInfo, instancesStateInfo]);

  useEffect(() => {
    if (extraInfo?.requestStatus === 'fulfilled') {
      if (isForceUpgrade) {
        setShowUpgrade(true);
      } else {
        setShowUpgrade(false);
      }
      if (
        isExpired ||
        isForceUpgrade ||
        !isActive ||
        isUpgrading ||
        isNoAccess
      ) {
        setShowEnter(false);
        if (isExpired) {
          setMessage(
            `${t('common.expandedRow.actionMessage.expiredInstance')}`
          );
        } else if (isForceUpgrade) {
          setMessage(`${t('common.expandedRow.actionMessage.forceUpgrade')}`);
        } else if (!isActive || isUpgrading) {
          setMessage(
            `${t('common.expandedRow.actionMessage.inactiveInstance')}`
          );
        } else if (isNoAccess) {
          setMessage(`${t('common.expandedRow.actionMessage.noAccess')}`);
        }
      } else {
        setShowEnter(true);
        setMessage('');
      }
    }
  }, [
    isNoAccess,
    isExpired,
    isForceUpgrade,
    isActive,
    isUpgrading,
    t,
    extraInfo
  ]);
  return (
    <Stack direction="column" spacing={6}>
      <div
        style={{
          visibility: !showEnter ? 'visible' : 'hidden',
          height: '16px',
          color: 'var(--blue)',
          fontWeight: 'bold'
        }}
      >
        {message}
      </div>
      <ButtonToolbar style={{ justifyContent: 'center' }}>
        <>
          <Button
            startIcon={<AiOutlineApi />}
            className="action-btn"
            appearance="ghost"
            onClick={() => setOpenApi(true)}
            style={{ display: showApi ? 'block' : 'none' }}
          >
            {'API'}
          </Button>
          <DraggableDrawer
            className="api-portal-drawer"
            open={openApi}
            setOpen={setOpenApi}
            size="md"
            title={<b>{t('common.expandedRow.userAPIHelper')}</b>}
          >
            <ApiHelper rowData={rowData} />
          </DraggableDrawer>
        </>
        <Button
          startIcon={<IoEnterOutline />}
          className="action-btn"
          appearance="primary"
          disabled={!showEnter || STATUS.includes(stateInfo.state)}
          onClick={async () => {
            const data = await getInstanceUrl({
              account: rowData
            }).unwrap();
            redirectVmUrl(data, '_blank');
          }}
        >
          {t('common.expandedRow.enter')}
        </Button>
        <>
          {showUpgrade && (
            <OsUpgrade
              isNoAccess={isNoAccess}
              extraInfo={extraInfo}
              rowData={rowData}
              action={actionConst.upgrade}
              server={server}
            />
          )}
        </>
        {instance?.enable_snapshot &&
          (rowData?.all_assets_access || instance?.is_fullaccess) && (
            <>
              <FunctionMenu rowData={rowData} instance={instance} />
            </>
          )}
      </ButtonToolbar>
    </Stack>
  );
};

export const redirectVmUrl = (
  data: Record<string, any>,
  target?: string
): boolean => {
  const { url, token, method } = data;
  if (url) {
    const safeUrl = getSafeUrl(url);
    if (method === 'POST' && token) {
      console.info('Redirecting to VM URL via POST:', safeUrl);

      // ---- Build a POST form ----
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = safeUrl;
      form.target = target || '_blank';

      // Add token
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'token';
      input.value = token;
      form.appendChild(input);

      // Add form to page
      document.body.appendChild(form);

      // Submit
      form.submit();

      // Remove form
      document.body.removeChild(form);
      return true;
    } else if (window) {
      const win = window.open(safeUrl, target || '_blank');
      if (win === null) {
        console.error(
          'Failed to open new window for VM URL. Your browser may have blocked it.'
        );
        return false;
      }
      return true;
    }
  }
  return false;
};
